"use client";

import * as React from "react";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  getAdminNavigationPathname,
  isAdminNavigationPathActive,
  type AdminNavigationGroup,
} from "@/lib/adminNavigation";
import { usePathname } from "@/lib/router-compat";

export function NavigationSettingsEntry({
  group,
  onOpen,
}: {
  group?: AdminNavigationGroup;
  onOpen: () => void;
}) {
  if (!group) return null;

  const GroupIcon = group.icon;

  return (
    <SidebarMenu initial={false}>
      <SidebarMenuItem>
        <SidebarMenuButton
          type="button"
          size="lg"
          tooltip={group.label}
          aria-label={`${group.label}を開く`}
          onClick={onOpen}
          className="cursor-pointer font-normal"
        >
          <span
            data-slot="settings-entry-icon"
            className="bg-muted flex size-8 shrink-0 items-center justify-center rounded-lg group-data-[collapsible=icon]:bg-transparent"
          >
            <GroupIcon className="size-4" aria-hidden="true" />
          </span>
          <span className="group-data-[collapsible=icon]:hidden">
            {group.label}
          </span>
          <ChevronRight
            className="ml-auto group-data-[collapsible=icon]:hidden"
            aria-hidden="true"
          />
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function NavigationSettings({
  group,
}: {
  group?: AdminNavigationGroup;
}) {
  const pathname = usePathname();
  const { isMobile, setOpenMobile } = useSidebar();
  const activeDestinationId = React.useMemo(() => {
    if (!group) return undefined;

    return group.items
      .flatMap((item) => (item.kind === "folder" ? item.items : [item]))
      .filter((link) => isAdminNavigationPathActive(pathname, link.url))
      .sort(
        (left, right) =>
          getAdminNavigationPathname(right.url).length -
          getAdminNavigationPathname(left.url).length
      )[0]?.id;
  }, [group, pathname]);
  const activeCategory = group?.items.find(
    (item) =>
      item.kind === "folder" &&
      item.items.some((link) => link.id === activeDestinationId)
  );
  const [expandedCategoryId, setExpandedCategoryId] = React.useState<
    string | null
  >(() => (activeCategory?.kind === "folder" ? activeCategory.id : null));

  React.useEffect(() => {
    if (activeCategory?.kind === "folder") {
      setExpandedCategoryId(activeCategory.id);
    }
  }, [activeCategory]);

  if (!group) return null;

  const handleNavigate = () => {
    if (isMobile) setOpenMobile(false);
  };

  return (
    <nav aria-label={`${group.label}メニュー`}>
      <SidebarGroup className="pt-1">
        <SidebarGroupLabel className="font-medium">
          {group.label}
        </SidebarGroupLabel>
        <SidebarMenu initial={false}>
          {group.items.map((item) => {
            const ItemIcon = item.icon ?? group.icon;
            const itemActive =
              item.kind === "folder"
                ? item.items.some((link) => link.id === activeDestinationId)
                : item.id === activeDestinationId;

            if (item.kind === "link") {
              return (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    asChild
                    tooltip={item.title}
                    isActive={itemActive}
                    className="min-h-10 font-normal md:min-h-8"
                  >
                    <Link
                      to={item.url}
                      aria-current={itemActive ? "page" : undefined}
                      onClick={handleNavigate}
                    >
                      {ItemIcon ? <ItemIcon aria-hidden="true" /> : null}
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            }

            const expanded = expandedCategoryId === item.id;
            const panelId = `settings-category-${item.id}`;

            return (
              <SidebarMenuItem key={item.id}>
                <SidebarMenuButton
                  type="button"
                  tooltip={item.title}
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  isActive={itemActive && !expanded}
                  onClick={() =>
                    setExpandedCategoryId(expanded ? null : item.id)
                  }
                  className="min-h-10 cursor-pointer font-medium md:min-h-9"
                >
                  <ItemIcon aria-hidden="true" />
                  <span>{item.title}</span>
                  <ChevronRight
                    className={`ml-auto transition-transform duration-200 motion-reduce:transition-none ${
                      expanded ? "rotate-90" : ""
                    }`}
                    aria-hidden="true"
                  />
                </SidebarMenuButton>
                {expanded ? (
                  <div id={panelId}>
                    <SidebarMenuSub>
                      {item.items.map((link) => {
                        const linkActive = link.id === activeDestinationId;
                        return (
                          <SidebarMenuSubItem key={link.id}>
                            <SidebarMenuSubButton
                              asChild
                              isActive={linkActive}
                              className="min-h-10 font-normal md:min-h-7"
                            >
                              <Link
                                to={link.url}
                                aria-current={linkActive ? "page" : undefined}
                                onClick={handleNavigate}
                              >
                                <span>{link.title}</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        );
                      })}
                    </SidebarMenuSub>
                  </div>
                ) : null}
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroup>
    </nav>
  );
}
